import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {AccountSecurityLevel, CompanySecurityRequirement} from './account-security.models';

/**
 * The two company-policy endpoints of `AccountController` (#282), which carries `[Route("/auth/account")]`:
 *
 * * `GET  /auth/account/admin/companies/{companyId}/security-level`
 * * `POST /auth/account/admin/companies/{companyId}/security-level`
 *
 * Single `BASE_URL` origin; `AuthInterceptor` attaches the context token and the `CM-Secret` cookie rides along,
 * so nothing here deals with credentials.
 *
 * **The company is a path parameter, not something the token fixes** — which is exactly why the controller pairs
 * `[Authorize(Roles = PlatformOwner, CompanyOwner)]` with a private `IsOwnCompanyOrPlatform(companyId)` check.
 * The panel therefore always sends the company its own session names, and renders nothing at all when the
 * session has none (a platform owner outside a tenant); an empty segment would produce
 * `…/companies//security-level`, which is a routing failure dressed up as a permission problem.
 */
@Injectable({providedIn: 'root'})
export class AccountSecurityHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  /** What `companyId` currently requires of the accounts entering it. */
  get(companyId: string): Observable<CompanySecurityRequirement> {
    return this.http.get<CompanySecurityRequirement>(this.url(companyId));
  }

  /**
   * Sets what `companyId` requires.
   *
   * Sends the enum **name** (`'TwoFactor'`), never its ordinal: the server parses with `Enum.TryParse` and
   * answers a bodyless `400` for anything else — see the note at the top of `account-security.models.ts`.
   */
  set(companyId: string, level: AccountSecurityLevel): Observable<void> {
    return this.http.post<void>(this.url(companyId), {requiredLevel: level} satisfies CompanySecurityRequirement);
  }

  private url(companyId: string): string {
    return `${this.baseUrl}/auth/account/admin/companies/${encodeURIComponent(companyId)}/security-level`;
  }
}
